import { useEffect, useMemo, useState } from 'react';
import { View, Modal, ScrollView, Pressable, Alert, ActivityIndicator, Platform, KeyboardAvoidingView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { InputField } from '@/components/ui/InputField';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { SelectFieldModalProvider } from '@/components/ui/SelectFieldModalContext';
import { ModalHeader } from '@/components/ui/ModalHeader';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import { DocumentFormFields } from './DocumentFormFields';
import type { Document, DocumentMetadata, DocumentType } from '@/services/documentVault';
import { updateDocument } from '@/services/documentVault';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';

type DocumentEditModalProps = {
  visible: boolean;
  document: Document | null;
  onClose: () => void;
};

const DOCUMENT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'driving_license', label: 'Driving License' },
  { value: 'health_record', label: 'Health Record' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'other', label: 'Other' },
];

const computeExpiresOn = (type: DocumentType, metadata: Partial<DocumentMetadata> | null) => {
  const meta: any = metadata || {};
  if (type === 'insurance' && meta.coverageEndDate instanceof Date) return meta.coverageEndDate as Date;
  if (meta.expirationDate instanceof Date) return meta.expirationDate as Date;
  return null;
};

export function DocumentEditModal({ visible, document, onClose }: DocumentEditModalProps) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();

  const [name, setName] = useState('');
  const [type, setType] = useState<DocumentType>('other');
  const [metadata, setMetadata] = useState<Partial<DocumentMetadata>>({});
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>();

  // Hydrate local state when opening
  useEffect(() => {
    if (!visible || !document) return;
    setName(document.name || '');
    setType((document.type || 'other') as DocumentType);
    setMetadata((document.metadata || {}) as Partial<DocumentMetadata>);
    setSaving(false);
    setNameError(undefined);
  }, [visible, document?.id]);

  const inferredExpires = useMemo(() => computeExpiresOn(type, metadata), [type, metadata]);

  const handleSave = async () => {
    if (!document) return;
    if (!name.trim()) {
      setNameError('Enter a document name.');
      return;
    }
    setNameError(undefined);

    // Single source of truth: infer expiresOn from document metadata.
    const nextExpires = inferredExpires;

    setSaving(true);
    try {
      await updateDocument(document.id, {
        name: name.trim(),
        type,
        expiresOn: nextExpires,
        metadata: (metadata as DocumentMetadata) || null,
      });
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert('Document updated.');
      } else {
        Alert.alert('Saved', 'Document updated.');
      }
      onClose();
    } catch (e: any) {
      Alert.alert('Unable to save', humanizeAsyncError(e, 'We couldn’t update this document. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  if (!document) return null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      // iOS does not support `transparent` with `pageSheet` (it silently fails / warns).
      // Use `overFullScreen` for consistent bottom-sheet behavior across platforms.
      presentationStyle={Platform.OS === 'ios' ? 'fullScreen' : 'overFullScreen'}
      statusBarTranslucent
    >
      <SelectFieldModalProvider>
      <View style={{ flex: 1, backgroundColor: theme.colors.surface, justifyContent: 'flex-start' }}>
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'height' : undefined}
          keyboardVerticalOffset={0}
          style={{ flex: 1 }}
        >
          <View
            style={{
              flex: 1,
              backgroundColor: theme.colors.surface,
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              overflow: 'hidden',
            }}
          >
            <ModalHeader title="Edit Document" onClose={onClose} />

            <ScrollView
              style={{ flex: 1 }}
              contentContainerStyle={{
                padding: theme.spacing(2),
                gap: theme.spacing(2),
                // Keep content comfortably above the sticky action bar.
                paddingBottom: insets.bottom + theme.spacing(12),
              }}
              keyboardShouldPersistTaps="handled"
              keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
              automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
            >
              {document.entryType === 'upload' ? (
                <Card variant="outlined">
                  <Caption color={theme.colors.textSecondary}>
                    You can edit the details below. The attached file stays the same.
                  </Caption>
                </Card>
              ) : null}

              <InputField
                label="Document Name"
                placeholder="Enter document name"
                value={name}
                onChangeText={(value) => {
                  setName(value);
                  if (nameError) setNameError(undefined);
                }}
                editable={!saving}
                errorText={nameError}
              />

              <SelectField
                label="Document Type"
                value={type}
                options={DOCUMENT_TYPE_OPTIONS}
                onValueChange={(value) => setType(value as DocumentType)}
              />

              <DocumentFormFields
                documentType={type}
                metadata={metadata}
                onMetadataChange={setMetadata}
                disabled={saving}
              />
            </ScrollView>

            <View
              style={{
                padding: theme.spacing(2),
                borderTopWidth: 1,
                borderTopColor: theme.colors.borderMuted,
                paddingBottom: insets.bottom + theme.spacing(2),
                gap: theme.spacing(1),
              }}
            >
              <Button
                label={saving ? 'Saving…' : 'Save Changes'}
                onPress={handleSave}
                disabled={saving}
                icon={saving ? <ActivityIndicator size="small" color={theme.colors.surface} /> : undefined}
              />
              <Button label="Cancel" variant="secondary" onPress={onClose} disabled={saving} />
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
      </SelectFieldModalProvider>
    </Modal>
  );
}


